/**
 * Shared filter-bar sizing — ONE source of truth so the Field Sales, Efforts,
 * Sales Force Effectiveness and Division Performance filter bars stay in sync.
 *
 * The values are copied verbatim from the Field Sales filter bar
 * (`FieldSalesFilterBar.jsx`) + the shared `MultiSelectFilter`, which is the
 * reference look:
 *   - container Paper: padding 2.5 (20px), radius 16, soft 2-layer shadow
 *   - label: 0.7rem / 700 / uppercase / letter-spacing .06em / text.secondary
 *   - control: MUI `size="small"` (~40px tall), radius 8, value text 13px
 *   - grid/flex gap: 2 (16px), controls bottom-aligned
 *   - Reset button: height 40, minWidth 96, 13px / 600, radius 8
 *
 * Spread these fragments into each bar so a change here updates all four.
 */

/** Outer <Paper variant="outlined"> wrapper for every filter bar. */
export const FILTER_BAR_PAPER_SX = {
  p: 2.5,
  mb: 3,
  bgcolor: 'background.paper',
  borderRadius: '16px',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 1px 2px rgba(16,24,40,.06), 0 1px 3px rgba(16,24,40,.10)',
};

/**
 * Field wrapper: label stacked above its control. `minWidth` lets a control
 * shrink to 0 on mobile (so nothing is clipped as the bar wraps) and sit at a
 * usable 150px from the `sm` breakpoint up.
 */
export const FILTER_FIELD_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  width: '100%',
  minWidth: { xs: 0, sm: 150 },
};

/** Uppercase caption shown above each control. */
export const FILTER_LABEL_SX = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'text.secondary',
};

/** Closed-select trigger: radius 8 and a 13px value, matching the reference. */
export const FILTER_SELECT_SX = {
  borderRadius: '8px',
  '& .MuiSelect-select': { fontSize: '13px' },
};

/** Row/grid gap between controls (16px), controls aligned to their bottom. */
export const FILTER_BAR_GAP = 2;

/** Apply/Reset button sizing so the action buttons line up with the controls. */
export const FILTER_BUTTON_SX = {
  height: 40,
  minWidth: 96,
  fontWeight: 600,
  fontSize: '13px',
  borderRadius: '8px',
};
